/**
 * User Preferences API Client
 * 
 * Client for interacting with the user-preferences API.
 * Uses tokens provided by the host application (following UI.Streaks pattern).
 */

import axios, { AxiosInstance } from 'axios';

/**
 * Verification status data structure.
 */
export interface VerificationStatus {
  isVerified: boolean;
  partnerDoingBusinessAs: string;
}

/**
 * API client for user preferences endpoints.
 * 
 * This client expects tokens to be provided by the host application.
 * It does not handle authentication itself - tokens should be passed to each method.
 */
export class VerificationApiClient {
  private axiosInstance: AxiosInstance;
  private readonly BASE_URL = process.env.REACT_APP_VERIFICATION_API_URL;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: this.BASE_URL,
      timeout: 30000, // 30 second timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Get user notification preferences by reference user ID.
   * 
   * @param accessToken - The access token provided by the host application
   * @returns Promise with array of user notification preferences
   * @throws AxiosError if the request fails
   */
  async userVerificationStatus(accessToken: string): Promise<VerificationStatus> {
    try {
      const response = await this.axiosInstance.get(
        '/verification/status',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Verification API returns an empty response body if the user is not verified.
      if (response.data === null) {
        return {
          isVerified: false,
          partnerDoingBusinessAs: "your business partner",
        };
      }

      return {
        isVerified: response.data.isVerified,
        partnerDoingBusinessAs: response.data.partner_doing_business_as ?? "your business partner",
      };
    } catch (error) {
      // Re-throw axios errors directly as requested
      throw error;
    }
  }
}

// Export a singleton instance for convenience
export const verificationApiClient = new VerificationApiClient();
